import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { hasTier } from '@/admin/access';
import { EditGroupDialog } from '@/admin/groups/EditGroupDialog';
import { groupErrorKey } from '@/admin/groups/errors';
import { GroupRoster } from '@/admin/groups/GroupRoster';
import { GROUP_TYPE_KEYS } from '@/admin/groups/groupTypes';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { PageTitle } from '@/admin/ui/Page';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api, { queryClient } from '@/api';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/admin/groups/$groupId')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  component: GroupDetail,
});

const DASH = '—';

function GroupDetail() {
  const { t } = useTranslation();
  const { groupId } = Route.useParams();
  const navigate = Route.useNavigate();
  const groupIdNum = Number(groupId);
  const { me } = useCurrentUser();
  const canWrite = hasTier(me, 'SUPPORT');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    data: group,
    isLoading,
    error,
  } = api.getAdminGroup.useQuery({ path: { group_id: groupIdNum } });

  const deleteMutation = api.deleteAdminGroup.useMutation(undefined, {
    meta: { toast: t('admin.common.deleted') },
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.invalidateQueries({
        queryKey: api.listAdminGroups.getQueryKey(),
      });
      navigate({ to: '/admin/groups/' });
    },
  });

  if (isLoading) {
    return <LoadingMessage>{t('admin.groups.loading')}</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{t('admin.groups.errorLoading')}</ErrorMessage>;
  }

  if (!group) {
    return <ErrorMessage>{t('admin.groups.notFound')}</ErrorMessage>;
  }

  const { address } = group;
  const place = [address.city ?? group.city, address.state ?? group.state]
    .filter(Boolean)
    .join(', ');

  return (
    <Container>
      <BackLink to="/admin/groups/">{t('admin.groups.backToList')}</BackLink>
      <PageTitle>
        {group.name ||
          t('admin.groups.titleFallback', {
            id: groupId,
            interpolation: { escapeValue: false },
          })}
      </PageTitle>

      <Section>
        <SectionHeader>
          <SectionTitle>{t('admin.common.details')}</SectionTitle>
          {canWrite && (
            <Controls>
              <Button
                className={buttonStyles.Button}
                onClick={() => setEditOpen(true)}
              >
                {t('admin.common.edit')}
              </Button>
              {!group.is_study_group && (
                <Button
                  className={buttonStyles.Button}
                  onClick={() => setDeleteOpen(true)}
                >
                  {t('admin.common.delete')}
                </Button>
              )}
            </Controls>
          )}
        </SectionHeader>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.common.id')}</FieldLabel>
            <FieldValue>{group.id}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.common.name')}</FieldLabel>
            <FieldValue>{group.name || DASH}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.fields.type')}</FieldLabel>
            <FieldValue>
              {group.group_type ? t(GROUP_TYPE_KEYS[group.group_type]) : DASH}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.fields.wbsCode')}</FieldLabel>
            <FieldValue>{group.wbs_code || DASH}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.fields.description')}</FieldLabel>
            <FieldValue>{group.description || DASH}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.totalMembers')}</FieldLabel>
            <FieldValue>{group.total_member_count ?? DASH}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>{t('admin.groups.addressSection')}</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>{t('admin.groups.fields.address')}</FieldLabel>
            <FieldValue>{address.address || DASH}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.location')}</FieldLabel>
            <FieldValue>{place || DASH}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.fields.country')}</FieldLabel>
            <FieldValue>{address.country ?? group.country ?? DASH}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>{t('admin.groups.fields.postCode')}</FieldLabel>
            <FieldValue>{address.post_code || DASH}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <GroupRoster groupId={groupIdNum} />
      </Section>

      <Section>
        <SectionTitle>{t('admin.groups.links')}</SectionTitle>
        <LinkRow>
          <Link
            to="/groups/$groupName/"
            params={{ groupName: group.slug }}
            className={buttonStyles.Button}
          >
            {t('admin.groups.viewGroup')}
          </Link>
          {group.is_study_group && (
            <>
              <Link
                to="/groups/$groupName/admin/release/"
                params={{ groupName: group.slug }}
                className={buttonStyles.Button}
              >
                {t('admin.groups.releaseStudents')}
              </Link>
              <Link
                to="/groups/$groupName/admin/substitute/"
                params={{ groupName: group.slug }}
                className={buttonStyles.Button}
              >
                {t('admin.groups.substituteStudents')}
              </Link>
            </>
          )}
          {(group.group_type === 'CONGREGATION' ||
            group.group_type === 'CUSTOM_STUDY_GROUP') && (
            <Link
              to="/groups/$groupName/group-notes/"
              params={{ groupName: group.slug }}
              className={buttonStyles.Button}
            >
              {t('admin.groups.groupNotes')}
            </Link>
          )}
        </LinkRow>
      </Section>

      {canWrite && editOpen && (
        <EditGroupDialog
          group={group}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
      {canWrite && !group.is_study_group && (
        <ConfirmDialog
          open={deleteOpen}
          title={t('admin.groups.deleteTitle')}
          tone="danger"
          confirmLabel={t('admin.common.delete')}
          pending={deleteMutation.isPending}
          onConfirm={() =>
            deleteMutation.mutate({ path: { group_id: groupIdNum } })
          }
          onCancel={() => {
            deleteMutation.reset();
            setDeleteOpen(false);
          }}
        >
          <p>
            {t('admin.groups.deleteBody', {
              name: group.name,
              interpolation: { escapeValue: false },
            })}
          </p>
          {deleteMutation.error && (
            <ErrorLine role="alert">
              {t(groupErrorKey(deleteMutation.error))}
            </ErrorLine>
          )}
        </ConfirmDialog>
      )}
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  align-items: center;
  color: var(--blue);
  display: inline-flex;
  font-size: 0.875rem;
  gap: 0.5rem;
  margin-bottom: 1rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const LinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ErrorLine = styled.p`
  color: var(--error);
  font-weight: 600;
`;

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
